/**
 * W10d — THE METRICS TAB'S TWO HALVES: the window, the one-answer rule, and the series.
 *
 * Pure. No transport, no React, no `Date.now()` — every function takes `now` so the rules can be
 * exercised deterministically instead of only ever answering for the machine they run on
 * (`ProgramWeekCalendar`'s own argument, one layer over).
 */

export const WINDOWS = ["4w", "3m", "all"] as const;
export type Window = (typeof WINDOWS)[number];

export function windowLabel(w: Window): string {
  switch (w) {
    case "4w":  return "4 weeks";
    case "3m":  return "3 months";
    case "all": return "All";
  }
}

/** The earliest instant a window admits, or null for "all" — **null means no floor, never 0**. */
export function windowStart(w: Window, now: Date): Date | null {
  const d = new Date(now.getTime());
  switch (w) {
    case "4w":  d.setDate(d.getDate() - 28); return d;
    case "3m":  d.setMonth(d.getMonth() - 3); return d;
    case "all": return null;
  }
}

/** Anything with a date. The two halves of this tab share the filter, so they share the type. */
export type Dated = { atISO: string };

/**
 * The entries inside the window, oldest first.
 *
 * ⚠️ AN UNPARSEABLE DATE IS DROPPED, NOT SORTED TO THE FRONT. `new Date("nonsense").getTime()` is
 * NaN, every comparison with it is false, and a NaN in a sort makes the ORDER undefined — so one
 * bad row would scramble a chart rather than be missing from it. Dropped and countable.
 */
export function inWindow<T extends Dated>(rows: readonly T[], w: Window, now: Date): T[] {
  const floor = windowStart(w, now);
  return rows
    .filter((r) => {
      const t = new Date(r.atISO).getTime();
      if (Number.isNaN(t)) return false;
      return floor === null || t >= floor.getTime();
    })
    .sort((a, b) => new Date(a.atISO).getTime() - new Date(b.atISO).getTime());
}

// ── THE CARD'S ONE-ANSWER RULE ──────────────────────────────────────────────────────────────────

export type OneAnswer =
  | { kind: "none" }
  | { kind: "single"; value: number; atISO: string }
  | { kind: "delta"; value: number; atISO: string; delta: number; sinceISO: string };

/**
 * The card's value, and a delta **only when the window holds two readings**.
 *
 * 🔴 THIS IS THE RULE, AND IT IS THE WHOLE POINT OF THE CARD. A delta computed against a reading
 * OUTSIDE the window is a number about a period the coach is not looking at — it would say
 * "−1.2 kg" under a heading that says "4 weeks" while the −1.2 happened over five months. And a
 * delta of 0 printed for a single reading is worse than none: it asserts no change where nothing
 * was compared.
 *
 * ⚠️ `single` AND `none` ARE DIFFERENT ANSWERS and the screen must draw them differently: one is
 * "here is where they are, with nothing to compare it to", the other is "nothing was recorded in
 * this window at all". Collapsing them is the empty-collection-stands-in-for-a-negative trap.
 */
export function oneAnswer(values: readonly { atISO: string; value: number | null }[]): OneAnswer {
  const known = values.filter((v): v is { atISO: string; value: number } => v.value !== null);
  if (known.length === 0) return { kind: "none" };
  const last = known[known.length - 1]!;
  if (known.length === 1) return { kind: "single", value: last.value, atISO: last.atISO };
  const first = known[0]!;
  return {
    kind: "delta",
    value: last.value,
    atISO: last.atISO,
    delta: last.value - first.value,
    sinceISO: first.atISO,
  };
}

/**
 * The delta, spoken.
 *
 * ⚠️ A SIGN IS ALWAYS SHOWN, INCLUDING ON ZERO. "0.0" beside a weight reads as a missing value;
 * "+0.0" reads as measured and unchanged, which is what it is — and unchanged over four weeks is
 * information a coach acts on.
 */
export function describeDelta(delta: number, unit: string, digits = 1): string {
  const sign = delta > 0 ? "+" : delta < 0 ? "−" : "+";
  return `${sign}${Math.abs(delta).toFixed(digits)} ${unit}`;
}

// ── EXERCISE METRICS ────────────────────────────────────────────────────────────────────────────

/** One logged set, as the coach's reads already shape them. */
export type LoggedSetPoint = {
  atISO: string;
  weightKg: number | null;
  reps: number | null;
  holdSeconds: number | null;
};

/**
 * The BEST set in a window: heaviest by estimated 1RM where reps and weight exist, else the
 * longest hold.
 *
 * ⚠️ THE TWO KINDS ARE NEVER COMPARED WITH EACH OTHER. Seconds and kilos are different units and
 * a "best" that ranks a 40-second hold against a 60 kg triple is a number with no meaning. An
 * exercise that has both (rare, but the catalogue allows it) reports the LOADED one, because that
 * is the one an e1RM line can be drawn for — and the screen says which it is.
 */
export type BestSet =
  | { kind: "loaded"; weightKg: number; reps: number; e1rm: number; atISO: string }
  | { kind: "hold"; seconds: number; atISO: string }
  | { kind: "none" };

export function bestSet(
  sets: readonly LoggedSetPoint[],
  e1rmOf: (weightKg: number | null, reps: number | null) => number | null,
): BestSet {
  let best: BestSet = { kind: "none" };
  let bestE = -1;
  for (const s of sets) {
    const e = e1rmOf(s.weightKg, s.reps);
    if (e !== null && e > bestE) {
      bestE = e;
      best = { kind: "loaded", weightKg: s.weightKg!, reps: s.reps!, e1rm: e, atISO: s.atISO };
    }
  }
  if (best.kind === "loaded") return best;
  let bestS = -1;
  for (const s of sets) {
    if (s.holdSeconds !== null && s.holdSeconds > bestS) {
      bestS = s.holdSeconds;
      best = { kind: "hold", seconds: s.holdSeconds, atISO: s.atISO };
    }
  }
  return best;
}

/**
 * Exercises the athlete has LOGGED, most recently logged first.
 *
 * 🔴 FROM THE SET LOGS, NEVER FROM A STATUS. The roster's own lesson, measured on this database:
 * `completed_at` is set on 7 sessions against 52 that say `status='completed'`, and one real
 * client has ZERO of both while having logged 44 sets. **The correct source is the one that
 * records somebody DOING something.**
 */
export function byLastLogged<T extends { sets: readonly LoggedSetPoint[] }>(rows: readonly T[]): T[] {
  const lastOf = (r: T) =>
    r.sets.reduce((m, s) => {
      const t = new Date(s.atISO).getTime();
      return Number.isNaN(t) ? m : Math.max(m, t);
    }, -Infinity);
  return [...rows]
    .filter((r) => lastOf(r) !== -Infinity)   // an exercise with no datable set has not been logged
    .sort((a, b) => lastOf(b) - lastOf(a));
}
